/**
 * CharacterPreview Custom Element (headless)
 *
 * A non-visual API bridge that turns character inputs into a generated
 * preview image via the preview API. It renders nothing: consumers own all
 * display (buttons, spinners, image elements, error messaging) and drive it
 * through the public API below.
 *
 * Public API:
 *   - data: { illustrationId, characterId, faceUrl, eyeColor, age, turnstileContainer } (get/set)
 *   - illustrationId: string                                     (get/set)
 *   - characterId: string                                        (get/set)
 *   - state: "idle" | "loading" | "success" | "error"            (readonly)
 *   - onImage: (imageUrl: string) => void                        (callback)
 *   - requestPhoto(): Promise<string>  resolves the generated image URL
 *
 * Events (bubbling, composed):
 *   - "character-preview:state"  detail: { state, previousState }
 *   - "character-preview:image"  detail: { imageUrl }
 *   - "character-preview:error"  detail: { error, reason }
 */

const CHARACTER_PREVIEW_DEFAULTS = {
  baseUrl: "https://book-api-production-298d.up.railway.app/",
  pollIntervalMs: 2000,
};

const CHARACTER_PREVIEW_TERMINAL_STATUSES = new Set([
  "completed",
  "partial",
  "failed",
]);

function previewApiUrl(baseUrl, path) {
  return new URL(path, baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`);
}

async function previewApiFetch(baseUrl, path, init = {}) {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(previewApiUrl(baseUrl, path), {
    ...init,
    headers,
  });
  const text = await response.text();
  let data = null;

  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    throw new Error(
      `Invalid preview API response (${response.status}): ${text.slice(0, 500)}`,
    );
  }

  if (!response.ok) {
    const message =
      data && typeof data.error === "string"
        ? `${data.error}${data.errorCode ? ` (${data.errorCode})` : ""}`
        : text || response.statusText;
    throw new Error(
      `Preview API request failed: ${response.status} ${message}`,
    );
  }

  return data;
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function buildReferenceImageUrl(faceUrl) {
  if (/^https?:\/\//i.test(faceUrl)) {
    return faceUrl;
  }

  const s3Key = faceUrl.startsWith("s3://")
    ? faceUrl
    : `s3://childbook-photo-uploads/${faceUrl.replace(/^\/+/, "")}`;

  return `https://images.childbook.ai/sig/${base64EncodeS3Key(s3Key)}`;
}

function base64EncodeS3Key(s3Key) {
  const parts = s3Key.split("/");
  const fileName = parts.pop();
  const safeKey = fileName
    ? `${parts.join("/")}/${encodeURIComponent(fileName)}`.replaceAll(
        "%20",
        " ",
      )
    : s3Key;

  return btoa(safeKey);
}

function buildFacePrompt({ age, eyeColor, gender, facePrompt }) {
  if (typeof facePrompt === "string" && facePrompt.trim()) {
    return facePrompt.trim();
  }

  const subject =
    gender === "male" ? "a boy" : gender === "female" ? "a girl" : "a child";
  const ageText = Number.isFinite(age) ? `${age} years old` : "";
  const eyeText = eyeColor ? `with ${eyeColor} eyes` : "";

  return [subject, ageText, eyeText].filter(Boolean).join(" ");
}

function buildClientRequestId() {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `character-preview-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Creates an illustration-template-render job, polls until it reaches a terminal
 * status, then returns the first generated image URL.
 */
async function fetchPreviewImage(data, { signal, onProgress } = {}) {
  const baseUrl = data.baseUrl || CHARACTER_PREVIEW_DEFAULTS.baseUrl;
  const turnstileToken = await ensureToken(data);
  /* Eine Anfrage mit ALLEN Figuren - zwei Ein-Figur-Renderings ergeben kein
     gueltiges Zwei-Kinder-Bild. Mehr als drei nimmt die API nicht an. */
  const mehrere = Array.isArray(data.replacements) ? data.replacements.slice(0, 3) : [];
  const createBody = {
    illustrationId: data.illustrationId,
    replacements: mehrere.length
      ? mehrere.map((r) => ({
          characterId: r.characterId,
          referenceImageUrl: buildReferenceImageUrl(r.faceUrl),
          facePrompt: buildFacePrompt(r),
        }))
      : [
          {
            characterId: data.characterId,
            referenceImageUrl: buildReferenceImageUrl(data.faceUrl),
            facePrompt: buildFacePrompt(data),
          },
        ],
    clientRequestId: data.clientRequestId || buildClientRequestId(),
    shopifyDomain: "wxs8a1-5g.myshopify.com",
  };

  // Alternatives Renderverfahren, falls gesetzt (A/B-Test ab 28.10.2026).
  if (data.workflow) createBody.workflow = data.workflow;

  if (Number.isFinite(data.seed)) {
    createBody.seed = data.seed;
  }

  if (turnstileToken) {
    createBody.captchaToken = turnstileToken;
  }

  const created = await previewApiFetch(
    baseUrl,
    "api/illustration-template-render/",
    {
      method: "POST",
      body: JSON.stringify(createBody),
      signal,
    },
  );

  if (!created?.runId) {
    throw new Error("Preview API create response did not contain a runId");
  }

  const pollIntervalMs =
    data.pollIntervalMs || CHARACTER_PREVIEW_DEFAULTS.pollIntervalMs;
  let status = null;

  do {
    await sleep(pollIntervalMs);
    if (signal?.aborted) {
      throw new DOMException("Preview request was aborted", "AbortError");
    }

    status = await previewApiFetch(
      baseUrl,
      `api/illustration-template-render/${created.runId}`,
      { method: "GET", signal },
    );

    if (typeof onProgress === "function" && status) {
      try {
        onProgress({ progress: status.progress, eta: status.eta });
      } catch {
        /* ignore progress callback errors */
      }
    }
  } while (!CHARACTER_PREVIEW_TERMINAL_STATUSES.has(status?.status));

  const results = await previewApiFetch(
    baseUrl,
    `api/illustration-template-render/${created.runId}/results`,
    { method: "GET", signal },
  );

  if (results?.status === "failed") {
    throw new Error(status?.errorMessage || "Preview generation failed");
  }

  const imageUrl = results?.resultImages?.find(
    (url) => typeof url === "string" && url,
  );
  if (!imageUrl) {
    throw new Error("Preview API results did not contain an image URL");
  }

  return imageUrl;
}

async function ensureToken(data) {
  const presetToken =
    typeof data.turnstileToken === "string" && data.turnstileToken.trim()
      ? data.turnstileToken.trim()
      : typeof data.captchaToken === "string" && data.captchaToken.trim()
        ? data.captchaToken.trim()
        : "";

  if (presetToken) return presetToken;

  const getToken = window.theme?.turnstile?.getToken;
  if (typeof getToken !== "function") return "";

  return (
    (await getToken({
      container: data.turnstileContainer,
      action: "create_preview",
    })) || ""
  );
}

if (!customElements.get("character-preview")) {
  customElements.define(
    "character-preview",
    class CharacterPreview extends HTMLElement {
      constructor() {
        super();
        this._data = this.normalizeData();
        this._state = "idle";
        this._requestToken = 0;
        this._onImage = null;
      }

      // ----- data -----
      get data() {
        return { ...this._data };
      }

      set data(value) {
        this._data = this.normalizeData(value);
        // Invalidate any in-flight request: inputs changed, so a pending
        // response is now stale and must not call onImage.
        this._requestToken++;
      }

      get illustrationId() {
        return this._data.illustrationId;
      }

      set illustrationId(value) {
        this.setDataField("illustrationId", value);
      }

      get characterId() {
        return this._data.characterId;
      }

      set characterId(value) {
        this.setDataField("characterId", value);
      }

      // ----- state (readonly) -----
      get state() {
        return this._state;
      }

      setState(next) {
        if (next === this._state) return;
        const previousState = this._state;
        this._state = next;
        this.dispatchEvent(
          new CustomEvent("character-preview:state", {
            bubbles: true,
            composed: true,
            detail: { state: next, previousState },
          }),
        );
      }

      // ----- onImage callback -----
      get onImage() {
        return this._onImage;
      }

      set onImage(fn) {
        this._onImage = typeof fn === "function" ? fn : null;
      }

      // ----- public request -----
      /**
       * Requests a preview image for the current data.
       * @returns {Promise<string>} resolves with the generated image URL.
       */
      async requestPhoto() {
        const data = this._data;

        const missing = this.getMissingFields(data);
        if (missing.length > 0) {
          const error = new Error(
            `Missing required preview data: ${missing.join(", ")}`,
          );
          this.setState("error");
          this.dispatchEvent(
            new CustomEvent("character-preview:error", {
              bubbles: true,
              composed: true,
              detail: { error, reason: "missing-data", missing },
            }),
          );
          throw error;
        }

        const token = ++this._requestToken;
        this.setState("loading");

        try {
          const self = this;
          const imageUrl = await fetchPreviewImage(
            { ...data },
            {
              onProgress(info) {
                self.dispatchEvent(
                  new CustomEvent("character-preview:progress", {
                    bubbles: true,
                    composed: true,
                    detail: info,
                  }),
                );
              },
            },
          );

          // Drop stale responses: data changed (token bumped) while in flight.
          if (token !== this._requestToken) {
            return imageUrl;
          }

          this.setState("success");

          if (this._onImage) {
            try {
              this._onImage(imageUrl);
            } catch (callbackError) {
              console.error(
                "character-preview onImage callback error:",
                callbackError,
              );
            }
          }

          this.dispatchEvent(
            new CustomEvent("character-preview:image", {
              bubbles: true,
              composed: true,
              detail: { imageUrl },
            }),
          );

          return imageUrl;
        } catch (error) {
          // Ignore failures from a request that has since been superseded.
          if (token !== this._requestToken) {
            throw error;
          }

          this.setState("error");
          this.dispatchEvent(
            new CustomEvent("character-preview:error", {
              bubbles: true,
              composed: true,
              detail: { error, reason: "request-failed" },
            }),
          );
          throw error;
        }
      }

      // ----- helpers -----
      normalizeData(value = {}) {
        const next = value || {};
        return {
          baseUrl:
            this.getStringValue(next.baseUrl, "data-api-base-url") ||
            CHARACTER_PREVIEW_DEFAULTS.baseUrl,
          illustrationId: this.getStringValue(
            next.illustrationId,
            "data-illustration-id",
          ),
          characterId: this.getStringValue(
            next.characterId,
            "data-character-id",
          ),
          faceUrl: typeof next.faceUrl === "string" ? next.faceUrl.trim() : "",
          eyeColor:
            typeof next.eyeColor === "string" ? next.eyeColor.trim() : "",
          age: this.parseAge(next.age),
          gender: typeof next.gender === "string" ? next.gender.trim() : "",
          facePrompt:
            typeof next.facePrompt === "string" ? next.facePrompt.trim() : "",
          clientRequestId:
            typeof next.clientRequestId === "string"
              ? next.clientRequestId.trim()
              : "",
          turnstileContainer:
            next.turnstileContainer instanceof HTMLElement
              ? next.turnstileContainer
              : null,
          turnstileToken:
            typeof next.turnstileToken === "string"
              ? next.turnstileToken.trim()
              : "",
          captchaToken:
            typeof next.captchaToken === "string"
              ? next.captchaToken.trim()
              : "",
          // Alternatives Renderverfahren (A/B ab 28.10.2026). Muss HIER
          // stehen: normalizeData baut ein Objekt mit fester Feldliste, alles
          // andere wird beim Setzen von data verworfen - ohne diesen Eintrag
          // kam workflow nie im Anfragekoerper an. Nur der vom Backend
          // erlaubte Wert geht durch, sonst leer.
          workflow: next.workflow === "muse-image" ? "muse-image" : "",
          // Mehrere Figuren in EINER Anfrage (Zwei-Kinder-Buecher). Jeder
          // Eintrag: { characterId, faceUrl, eyeColor, age, gender }. Die
          // Reihenfolge ist die Slot-Reihenfolge und wird NICHT sortiert.
          // Leer -> die flachen Felder oben gelten, alles bleibt wie bisher.
          replacements: Array.isArray(next.replacements)
            ? next.replacements.filter((r) => r && r.characterId && r.faceUrl)
            : [],
          seed: this.parseOptionalNumber(next.seed),
          pollIntervalMs:
            this.parseOptionalNumber(next.pollIntervalMs) ||
            this.parseOptionalNumber(
              this.getAttribute("data-poll-interval-ms"),
            ) ||
            CHARACTER_PREVIEW_DEFAULTS.pollIntervalMs,
        };
      }

      setDataField(fieldName, value) {
        this._data = this.normalizeData({
          ...this._data,
          [fieldName]: typeof value === "string" ? value.trim() : "",
        });
        this._requestToken++;
      }

      getStringValue(value, attributeName) {
        if (typeof value === "string" && value.trim()) {
          return value.trim();
        }

        const attributeValue = this.getAttribute(attributeName);
        return attributeValue?.trim() || "";
      }

      parseAge(value) {
        if (value === null || value === undefined || value === "") return null;
        const num = typeof value === "number" ? value : Number(value);
        return Number.isFinite(num) ? num : null;
      }

      parseOptionalNumber(value) {
        if (value === null || value === undefined || value === "")
          return undefined;
        const num = typeof value === "number" ? value : Number(value);
        return Number.isFinite(num) ? num : undefined;
      }

      /* Bei mehreren Figuren stehen faceUrl, eyeColor, age und characterId je
         Eintrag in replacements - die flachen Felder sind dann leer und
         duerfen nicht eingefordert werden. Genau daran scheiterte die
         Zwei-Kinder-Vorschau am 01.11.2026 ("Missing required preview data"),
         obwohl alle Angaben da waren. illustrationId gilt in beiden Faellen. */
      getMissingFields(data) {
        const d = data || {};
        const missing = [];
        if (!d.illustrationId || typeof d.illustrationId !== "string")
          missing.push("illustrationId");

        const mehr = Array.isArray(d.replacements) ? d.replacements : [];
        if (mehr.length) {
          mehr.forEach((r, i) => {
            const p = `replacements[${i}].`;
            if (!r || !r.faceUrl || typeof r.faceUrl !== "string") missing.push(p + "faceUrl");
            if (!r || !r.eyeColor || typeof r.eyeColor !== "string") missing.push(p + "eyeColor");
            if (!r || !Number.isFinite(Number(r.age))) missing.push(p + "age");
            if (!r || !r.characterId || typeof r.characterId !== "string") missing.push(p + "characterId");
          });
          return missing;
        }

        if (!d.faceUrl || typeof d.faceUrl !== "string") missing.push("faceUrl");
        if (!d.eyeColor || typeof d.eyeColor !== "string") missing.push("eyeColor");
        if (!Number.isFinite(d.age)) missing.push("age");
        if (!d.characterId || typeof d.characterId !== "string")
          missing.push("characterId");
        return missing;
      }
    },
  );
}
